import { getFormErrorMessage } from "@core/utility/get-error-message";
import { TransportTypeFormRule, TransportTypeFormSchema } from "@factory/form/form.rule";
import { useFormDefaultTransportType } from "@factory/form/form-default-transport-type.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseTransportTypeModel } from "@factory/model/query-model-transport-type";
import { 
  useQyDeleteTransportType, 
  useQyGetTransportTypeById, 
  useQyUpdateTransportType} from "@factory/query/transport-type.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditTransportType() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultTransportType();
  const navigate = useNavigate();
  const { mapUpdateTransportType, mapDeleteTransportType } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { transportTypeId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID TransportType
  const handleGetApiSuccess = (data: GetResponseTransportTypeModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
  };
  const {
    data: transportTypeResponse,
    isLoading,
    isError: transportTypeError,
  } = useQyGetTransportTypeById(transportTypeId || "", handleGetApiSuccess);
  const transportTypeData = transportTypeResponse?.data?.[0];

  // API UPDATE TransportType
  const handleUpdateApiSuccess = () => {
    showSuccess("TransportType updated successfully");
  };
  const { mutate: updateTransportType, isLoading: isEditLoading } = useQyUpdateTransportType(
    handleUpdateApiSuccess
  );

  // API DELETE TransportType
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("TransportType deleted successfully");
  };
  const { mutate: deleteTransportType } = useQyDeleteTransportType(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<TransportTypeFormSchema>({
    defaultValues: getDefault(transportTypeData),
    resolver: zodResolver(TransportTypeFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: TransportTypeFormSchema) => {
    if (!transportTypeData || !transportTypeData.code) {
      throw new Error("No transportType data");
    }

    const formData = mapUpdateTransportType(form, transportTypeData.code);
    updateTransportType(formData);
  };
  const handleValidateError = (err: FieldErrors<TransportTypeFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!transportTypeData || !transportTypeData.code) {
      throw new Error("No transportType data found");
    }

    const form = mapDeleteTransportType(transportTypeData.code);
    deleteTransportType(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    transportTypeId,
    transportTypeData,
    isLoading,
    transportTypeError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}